import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n";

const FIELDS = [
  { key: "name", label: "Nombre", type: "text", placeholder: "Tu nombre y apellido", autoComplete: "name", required: true },
  { key: "artist_name", label: "Nombre artístico (opcional)", type: "text", placeholder: "Como apareces en las plataformas", autoComplete: "nickname" },
  { key: "email", label: "Correo", type: "email", placeholder: "tu@correo.com", autoComplete: "email", required: true },
  { key: "password", label: "Contraseña", type: "password", placeholder: "Mínimo 8 caracteres", autoComplete: "new-password", required: true, minLength: 8 },
  { key: "confirm", label: "Confirmar contraseña", type: "password", autoComplete: "new-password", required: true },
];

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", artist_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError(t("Las contraseñas no coinciden"));
    setBusy(true); setError("");
    try {
      const { confirm, ...payload } = form;
      const u = await register(payload);
      toast.success(t("Cuenta creada. Bienvenido, {name}", { name: u.name }));
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="space-y-1">
          <p className="eyebrow">{t("Cuenta de artista")}</p>
          <h2 className="font-display text-2xl font-bold">{t("Crear cuenta")}</h2>
          <p className="text-sm text-muted-foreground">{t("Sigue el estado de tus lanzamientos y tus códigos UPC / ISRC.")}</p>
        </div>
        {FIELDS.map((f) => (
          <div className="space-y-2" key={f.key}>
            <Label htmlFor={`reg-${f.key}`}>{t(f.label)}</Label>
            <Input id={`reg-${f.key}`} type={f.type} required={f.required} minLength={f.minLength} placeholder={t(f.placeholder)}
              autoComplete={f.autoComplete} value={form[f.key]} onChange={set(f.key)} data-testid={`register-${f.key}-input`} />
          </div>
        ))}
        {error && <p data-testid="register-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-form-submit-button">{t(busy ? "Creando cuenta…" : "Crear cuenta")}</Button>
        <p className="text-sm text-muted-foreground text-center">
          {t("¿Ya tienes cuenta?")}{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-to-login-link">{t("Iniciar sesión")}</Link>
        </p>
      </form>
    </AuthShell>
  );
}
